import type { Config } from '@backstage/config';
import type { Entity } from '@backstage/catalog-model';

/**
 * Annotation carrying the component's Harbor repository. Its presence is the
 * marker for "this component has a container image", which is the precondition
 * for both Update (build a new tag) and Restart (redeploy the current tag).
 *
 * Templates stamp it only when there is an image to stamp: every registered
 * django component carries it and no mysql one does, so a database-only
 * component correctly gets neither button without any extra configuration.
 */
const CONTAINER_IMAGE_ANNOTATION = 'goharbor.io/repository-slug';

/**
 * Per-type scaffolder template references.
 * Either field may be omitted, in which case the corresponding action
 * button is hidden for that component type.
 */
export type ManagedComponentTemplates = {
  /** Used by Update, Restart, Delete (intent param at runtime). */
  primary?: string;
};

export type ManagedComponentType = {
  type: string;
  detect: {
    namePrefixes: string[];
    tags: string[];
  };
  templates: ManagedComponentTemplates;
  /**
   * Whether components of this type *can* support the Update action (a
   * Git-source rebuild via `source_type: git`). Opt-in: a type that omits it
   * gets no Update button, which is correct for e.g. the MySQL module, whose
   * template has no build.
   *
   * This is a ceiling, not the final answer -- an individual component must
   * also actually have an image. Use `ManagedComponentsResolver.supportsUpdate`
   * rather than reading this field directly.
   */
  supportsUpdate: boolean;
  /**
   * Whether components of this type *can* support the Restart action (re-deploy
   * of the current image tag via `source_type: harbor`). Opt-in, same
   * reasoning, and likewise a ceiling -- see
   * `ManagedComponentsResolver.supportsRestart`.
   */
  supportsRestart: boolean;
};

/**
 * Reads `scaffolder.managedComponents` from app-config and exposes a
 * couple of small helpers used by the catalog action buttons.
 *
 * The class is deliberately stateless and config-pull-on-demand — the
 * config doesn't change at runtime, and instantiation is cheap.
 */
export class ManagedComponentsResolver {
  constructor(private readonly config: Config) {}

  /** All managed-component types declared in app-config. */
  list(): ManagedComponentType[] {
    const entries =
      this.config.getOptionalConfigArray('scaffolder.managedComponents') ?? [];
    return entries.map(entry => ({
      type: entry.getString('type'),
      detect: {
        namePrefixes:
          entry.getOptionalStringArray('detect.namePrefixes') ?? [],
        tags: entry.getOptionalStringArray('detect.tags') ?? [],
      },
      templates: {
        primary: entry.getOptionalString('templates.primary') ?? undefined,
      },
      supportsUpdate: entry.getOptionalBoolean('supportsUpdate') ?? false,
      supportsRestart: entry.getOptionalBoolean('supportsRestart') ?? false,
    }));
  }

  /**
   * Classify an entity by name-prefix or tag. Returns null if no
   * managed-component type claims it (i.e. it's an unmanaged entity —
   * the action buttons should hide for it).
   */
  detectType(entity: Entity | undefined | null): ManagedComponentType | null {
    if (!entity) return null;
    const name = entity.metadata?.name ?? '';
    const tags = entity.metadata?.tags ?? [];

    for (const t of this.list()) {
      if (t.detect.namePrefixes.some(p => name.startsWith(p))) return t;
      if (t.detect.tags.some(tag => tags.includes(tag))) return t;
    }
    return null;
  }

  /** Look up `<type>` by its `type` string. */
  byType(typeName: string): ManagedComponentType | null {
    return this.list().find(t => t.type === typeName) ?? null;
  }

  /**
   * Whether this specific entity has a container image, i.e. whether the
   * image-based actions mean anything for it.
   */
  hasContainerImage(entity: Entity | undefined | null): boolean {
    return Boolean(entity?.metadata?.annotations?.[CONTAINER_IMAGE_ANNOTATION]);
  }

  /**
   * Whether the Update button should render for this entity.
   *
   * Two gates, because the capability is a property of the *component*, not
   * only of its type: the type must opt in (`supportsUpdate` in app-config),
   * and the component must actually have an image to rebuild. The second gate
   * is what lets one type cover both shapes -- a hosted service with an app
   * gets Update, a database-only hosted service in the same tier does not.
   */
  supportsUpdate(entity: Entity | undefined | null): boolean {
    return (
      (this.detectType(entity)?.supportsUpdate ?? false) &&
      this.hasContainerImage(entity)
    );
  }

  /**
   * Whether the Restart button should render for this entity. Same two gates as
   * `supportsUpdate`: Restart redeploys an existing Harbor tag, so a component
   * with no image has nothing to redeploy. (Bouncing a database pod would be a
   * different action, not this one.)
   */
  supportsRestart(entity: Entity | undefined | null): boolean {
    return (
      (this.detectType(entity)?.supportsRestart ?? false) &&
      this.hasContainerImage(entity)
    );
  }

  /**
   * Split a managed entity's `metadata.name` into the user-facing app
   * name and the owning user, using the detected type's name-prefix
   * rules. Naming convention is `<prefix><appName>-<user>`, e.g.
   * `django-webserver-blog-abc123` → `{ baseName: 'blog', targetUser: 'abc123' }`.
   *
   * Falls back to `{ baseName: fullName, targetUser: '' }` if no
   * managed type's prefix matches (e.g. a tag-only-detected entity, or
   * an unmanaged entity passed through by mistake — caller should
   * already have rendered nothing in that case via `detectType`).
   */
  parseEntityName(
    entity: Entity | undefined | null,
  ): { baseName: string; targetUser: string } {
    const fullName = entity?.metadata?.name ?? '';
    const detected = this.detectType(entity);
    const prefixes = detected?.detect.namePrefixes ?? [];
    for (const prefix of prefixes) {
      if (!fullName.startsWith(prefix)) continue;
      const withoutPrefix = fullName.slice(prefix.length);
      const lastDash = withoutPrefix.lastIndexOf('-');
      if (lastDash > 0) {
        return {
          baseName: withoutPrefix.substring(0, lastDash),
          targetUser: withoutPrefix.substring(lastDash + 1),
        };
      }
      return { baseName: withoutPrefix, targetUser: '' };
    }
    return { baseName: fullName, targetUser: '' };
  }
}
